/**
 * Client for the Wayfarer auth/sync backend (server/ in this repo, mounted at
 * <base>/api). Cookie-based sessions: the browser carries the httpOnly session
 * cookie, nothing token-like is stored in JS-visible storage.
 */

export interface User {
  id: number;
  name: string;
  role: 'admin' | 'driver';
  status: 'pending' | 'approved' | 'disabled';
}

export interface PendingUser {
  id: number;
  name: string;
  createdAt: string;
  status: User['status'];
  role: User['role'];
}

const API = `${import.meta.env.BASE_URL}api`;

class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API}${path}`, {
      ...init,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', ...init.headers },
      // A hung request must fail visibly, never stall the UI forever - flaky
      // car connectivity and broken HTTP/3 paths both present as hangs.
      signal: init.signal ?? AbortSignal.timeout(12_000),
    });
  } catch (err) {
    if (err instanceof DOMException && (err.name === 'TimeoutError' || err.name === 'AbortError')) {
      throw new ApiError('Server not responding - check your connection and try again.', 0);
    }
    throw new ApiError('Network error - are you online?', 0);
  }
  let body: any = null;
  try {
    body = await res.json();
  } catch {
    /* empty body is fine for some endpoints */
  }
  if (!res.ok) {
    throw new ApiError(body?.error ?? `Request failed (${res.status})`, res.status);
  }
  return body as T;
}

/* ------------------------------ session ---------------------------------- */

export function me(): Promise<{ user: User | null }> {
  return call('/me');
}

export function signup(name: string, pin: string): Promise<{ user: User }> {
  return call('/signup', { method: 'POST', body: JSON.stringify({ name, pin }) });
}

export function login(name: string, pin: string): Promise<{ user: User }> {
  return call('/login', { method: 'POST', body: JSON.stringify({ name, pin }) });
}

export function logout(): Promise<void> {
  return call('/logout', { method: 'POST' });
}

/* ------------------------------- admin ------------------------------------ */

export function adminListUsers(): Promise<{ users: PendingUser[] }> {
  return call('/admin/users');
}

export function adminSetStatus(
  userId: number,
  status: 'approved' | 'disabled',
): Promise<{ ok: true }> {
  return call(`/admin/users/${userId}/status`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });
}

export function adminResetPin(userId: number, pin: string): Promise<{ ok: true }> {
  return call(`/admin/users/${userId}/pin`, { method: 'POST', body: JSON.stringify({ pin }) });
}

/* ------------------------------- trips ------------------------------------ */

import type { Trip } from '../core/types';

/** Push a finished trip to the server (also kept in local IndexedDB). */
export function syncTrip(trip: Trip): Promise<{ ok: true }> {
  return call('/trips', { method: 'POST', body: JSON.stringify(trip) });
}

export function fetchTrips(): Promise<{ trips: Trip[] }> {
  return call('/trips');
}

/* --------------------------- shared saved places -------------------------- */

export type PlaceCategory = 'home' | 'work' | 'showroom' | 'depot' | 'other';

export interface SavedPlace {
  id: string;
  name: string;
  lng: number;
  lat: number;
  kind: 'place' | 'shared-location';
  category?: PlaceCategory;
  /**
   * Which folder it is filed under, or null/absent for unfiled.
   *
   * A free-form name rather than an id: folders are a filing label the fleet
   * agrees on ("Sauna customers", "Shops"), and a place whose folder no longer
   * exists reads as unfiled rather than disappearing into a dangling reference.
   */
  folder?: string | null;
  createdBy: number;
  creatorName: string;
  createdAt: string;
}

export function fetchPlaces(): Promise<{ places: SavedPlace[] }> {
  return call('/places');
}

export function createPlace(
  name: string,
  lng: number,
  lat: number,
  kind: 'place' | 'shared-location' = 'place',
  category: PlaceCategory = 'other',
  /** File it straight away; the server creates the folder if it is new. */
  folder: string | null = null,
): Promise<{ place: SavedPlace }> {
  return call('/places', {
    method: 'POST',
    body: JSON.stringify({ name, lng, lat, kind, category, folder }),
  });
}

export function deletePlace(id: string): Promise<{ ok: true }> {
  return call(`/places/${id}`, { method: 'DELETE' });
}

/** Rename a saved place; creator or admin only, enforced server-side. */
export function renamePlace(id: string, name: string): Promise<{ ok: true }> {
  return call(`/places/${id}/name`, { method: 'POST', body: JSON.stringify({ name }) });
}

/* --------------------------- folders for places ---------------------------- */

/** Every folder the fleet has, including ones with nothing in them yet. */
export function fetchFolders(): Promise<{ folders: string[] }> {
  return call('/folders');
}

export function createFolder(name: string): Promise<{ ok: true; existed: boolean }> {
  return call('/folders', { method: 'POST', body: JSON.stringify({ name }) });
}

/** Removes the folder only - the places inside it become unfiled, never deleted. */
export function deleteFolder(name: string): Promise<{ ok: true; unfiled: number }> {
  return call(`/folders/${encodeURIComponent(name)}`, { method: 'DELETE' });
}

/** File a place, or pass null to take it out of its folder. */
export function setPlaceFolder(id: string, folder: string | null): Promise<{ ok: true }> {
  return call(`/places/${id}/folder`, { method: 'POST', body: JSON.stringify({ folder }) });
}

/* ------------------------ Google Maps link resolver ------------------------ */

export interface ResolvedLink {
  url: string;
  name: string | null;
  lat: number;
  lng: number;
}

/**
 * Expand a short Google Maps share link (maps.app.goo.gl/...) server-side.
 * The redirect that reveals the coordinates cannot be followed from the
 * browser (cross-origin), so the API does it for us.
 */
export function resolveMapsLink(url: string, signal?: AbortSignal): Promise<ResolvedLink> {
  return call(`/resolve-link?url=${encodeURIComponent(url)}`, { signal });
}

/* ------------------------------- AI search -------------------------------- */

export interface AiSuggestion {
  /** What to feed the geocoder. */
  query: string;
  /** The name to show the driver. */
  label: string;
  /** One line on why it matches what they asked. */
  why: string;
}

/**
 * Ask the model to turn a loose description into searchable place names.
 *
 * Deliberately returns TERMS, not positions - the caller geocodes each one, so
 * every tappable result still comes from the map rather than the model.
 */
export function aiSearch(
  q: string,
  near: { lat: number; lng: number } | null,
  lang: string,
  signal?: AbortSignal,
): Promise<{ suggestions: AiSuggestion[] }> {
  return call('/ai/search', {
    method: 'POST',
    body: JSON.stringify({ q, lang, lat: near?.lat, lng: near?.lng }),
    signal,
  });
}

/* ------------------------------ push notifications ------------------------- */

/** The server's VAPID public key; 503 when push is unavailable there. */
export function fetchPushKey(): Promise<{ publicKey: string }> {
  return call('/push/key');
}

export function pushSubscribe(endpoint: string): Promise<{ ok: true }> {
  return call('/push/subscribe', { method: 'POST', body: JSON.stringify({ endpoint }) });
}

export function pushUnsubscribe(endpoint: string): Promise<{ ok: true }> {
  return call('/push/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint }) });
}

export { ApiError };
